import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

import { VeeraSidebar } from "@/components/chat/VeeraSidebar";
import { TrialPricingDialog } from "@/components/trial/TrialPricingDialog";
import { useAuth } from "@/hooks/useAuth";
import {
  LEADER_VEERA_PANEL_ID,
  LEADER_VEERA_PANEL_WIDTH_STYLE,
  useLeaderVeeraPanel,
} from "@/hooks/useLeaderVeeraPanel";
import { useTrialAccess } from "@/hooks/useTrialAccess";
import type { IntelligencePlanId } from "@/lib/intelligencePlans";
import { cn } from "@/lib/utils";
import { buildLeaderVeeraRoute } from "@/lib/veeraRoutes";

const ORDINARY_LEADER_ROUTE_PATTERNS = [
  "/today",
  "/moments",
  "/insights",
  "/shared-memory",
  "/shared-memory/:section",
  "/leader/coach",
  "/leader/talk-with-coach",
  "/reflection-companion",
  "/settings",
  "/apps",
  "/help",
  "/transcripts",
  "/transcript-upload",
  "/transcript-analysis/:transcriptId",
  "/post-meeting/:transcriptId",
  "/meetings/:meetingId",
  "/nudge/:id",
  "/assess/:meetingId",
  "/synthesis",
  "/calendar-upload",
  "/journey/summary/:summaryId",
  "/journey/reflections-due",
] as const;

const TRANSITION_LEADER_ROUTE_PATTERNS = [
  "/leadership-record",
  "/settings/leadership-record",
  "/leader-hub",
  "/journey",
  "/messages",
  "/integrations",
  "/transcript",
  "/dashboard",
  "/history",
  "/progress",
  "/focus",
  "/more",
] as const;

const matchesAny = (pathname: string, patterns: readonly string[]) => patterns.some(
  (pattern) => matchPath({ path: pattern, end: true }, pathname),
);

const isOrdinaryLeaderRoute = (pathname: string) => matchesAny(
  pathname,
  ORDINARY_LEADER_ROUTE_PATTERNS,
);

const isTransitionLeaderRoute = (pathname: string) => matchesAny(
  pathname,
  TRANSITION_LEADER_ROUTE_PATTERNS,
);

const contextLabelFor = (pageName: string) => {
  const label = pageName.replace(/[-_]+/g, " ").trim();
  return label ? `Using context from ${label.charAt(0).toUpperCase()}${label.slice(1)}` : undefined;
};

export function LeaderVeeraPanelHost() {
  const location = useLocation();
  const { user, roles, loading: authLoading } = useAuth();
  const panel = useLeaderVeeraPanel();
  const { hasOpened, resetBoundary } = panel;
  const authorizedUserIdRef = useRef<string | null>(null);
  const ordinaryRouteBoundaryRef = useRef(false);
  const hasLeaderRole = roles.includes("leader");
  const identityEligible = Boolean(user) && hasLeaderRole;
  const ordinaryRoute = isOrdinaryLeaderRoute(location.pathname);
  const transitionRoute = isTransitionLeaderRoute(location.pathname);

  useLayoutEffect(() => {
    if (ordinaryRoute && hasOpened) ordinaryRouteBoundaryRef.current = true;
  }, [hasOpened, ordinaryRoute]);

  const authorizedUserChanged = Boolean(
    authorizedUserIdRef.current && user?.id && authorizedUserIdRef.current !== user.id,
  );
  const routeEligible = ordinaryRoute || (transitionRoute && ordinaryRouteBoundaryRef.current);
  const identityBoundaryActive = authLoading
    ? Boolean(authorizedUserIdRef.current || identityEligible)
    : identityEligible;

  useEffect(() => {
    if (authorizedUserChanged) {
      authorizedUserIdRef.current = user?.id ?? null;
      ordinaryRouteBoundaryRef.current = false;
      if (hasOpened) resetBoundary();
      return;
    }

    if (identityEligible && user?.id) authorizedUserIdRef.current = user.id;
    if (!authLoading && !user) authorizedUserIdRef.current = null;
  }, [
    authLoading,
    authorizedUserChanged,
    identityEligible,
    hasOpened,
    resetBoundary,
    user,
  ]);

  useEffect(() => {
    if (!hasOpened) return;
    if (!routeEligible || (!authLoading && !identityEligible)) {
      ordinaryRouteBoundaryRef.current = false;
      resetBoundary();
    }
  }, [
    authLoading,
    identityEligible,
    hasOpened,
    resetBoundary,
    routeEligible,
  ]);

  if (
    authorizedUserChanged ||
    !hasOpened ||
    !routeEligible ||
    !identityBoundaryActive
  ) {
    return null;
  }

  return <LeaderVeeraPanelContent />;
}

function LeaderVeeraPanelContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const access = useTrialAccess({ role: "leader" });
  const panel = useLeaderVeeraPanel();
  const { closePanel, context, open, restoreFocus } = panel;
  const [hasDraft, setHasDraft] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const wasOpenRef = useRef(open);

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      requestAnimationFrame(() => restoreFocus());
    }
    wasOpenRef.current = open;
  }, [open, restoreFocus]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !hasDraft) closePanel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closePanel, hasDraft, open]);

  const state = access.presentation.state;
  const expired = state === "expired";
  const unknown = state === "unknown";
  const composerDisabled = access.isLoading
    || unknown
    || expired
    || (state !== "not_started" && !access.canCreatePremiumActivity);
  const lockedMessage = access.isLoading
    ? "Checking access…"
    : unknown
      ? "We couldn't confirm access."
      : expired
        ? "Your trial has ended. Your previous conversations and Leadership Record are still available."
        : state !== "not_started" && !access.canCreatePremiumActivity
          ? "Available with Intelligence"
          : undefined;
  const purchaseCtaAllowed = !access.isLoading
    && !["unknown", "coach_led", "billing_transfer_pending"].includes(state)
    && ["keep_full_access", "choose_a_plan", "view_plans"].includes(
      access.presentation.cta?.variant ?? "",
    );
  const purchaseCtaAllowedRef = useRef(purchaseCtaAllowed);

  useLayoutEffect(() => {
    purchaseCtaAllowedRef.current = purchaseCtaAllowed;
  }, [purchaseCtaAllowed]);

  useEffect(() => {
    if (!purchaseCtaAllowed && pricingOpen) setPricingOpen(false);
  }, [pricingOpen, purchaseCtaAllowed]);

  const pageContext = context
    ? {
        pageName: context.pageName,
        data: { source: context.source },
      }
    : { pageName: "leader" };

  const selectPlan = (planId: IntelligencePlanId) => {
    if (planId !== "intelligence_solo" && planId !== "leadership_record_solo") return;
    if (!purchaseCtaAllowedRef.current) {
      setPricingOpen(false);
      return;
    }
    setPricingOpen(false);
    closePanel();
    navigate(planId === "intelligence_solo" ? "/settings#plan-intelligence" : "/settings#billing");
  };

  return (
    <>
      <aside
        id={LEADER_VEERA_PANEL_ID}
        data-testid="leader-veera-panel"
        aria-label="Coach Veera"
        aria-hidden={!open}
        {...(!open ? { inert: "" } : {})}
        style={LEADER_VEERA_PANEL_WIDTH_STYLE}
        className={cn(
          "fixed inset-y-0 right-0 z-50 h-[100dvh] w-full bg-violet-50 transition-transform [transition-duration:250ms] ease-out motion-reduce:transition-none md:w-[var(--leader-veera-panel-width)] md:border-l md:border-violet-200 md:shadow-xl",
          open ? "translate-x-0" : "pointer-events-none translate-x-full",
        )}
      >
        <VeeraSidebar
          className="h-[100dvh] bg-violet-50"
          fullWidth
          surface="panel"
          pageContext={pageContext}
          contextLabel={context ? contextLabelFor(context.pageName) : undefined}
          onClose={closePanel}
          isActive={open}
          composerDisabled={composerDisabled}
          lockedMessage={lockedMessage}
          onViewPlans={purchaseCtaAllowed ? () => setPricingOpen(true) : undefined}
          onOpenFullPage={() => {
            closePanel();
            navigate(buildLeaderVeeraRoute({
              source: "leader-panel",
              from: location.pathname,
            }));
          }}
          onOpenSettings={() => {
            closePanel();
            navigate("/settings");
          }}
          onDraftStateChange={setHasDraft}
        />
      </aside>
      <TrialPricingDialog
        role="leader"
        open={pricingOpen && purchaseCtaAllowed}
        onOpenChange={(nextOpen) => setPricingOpen(nextOpen && purchaseCtaAllowedRef.current)}
        primaryLeaderId={null}
        onSelectPlan={selectPlan}
      />
    </>
  );
}
